import { useEffect, useState, useCallback } from "react";
import { api, apiError, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Users as UsersIcon, Trash2, Search, Pencil, Upload, ShieldOff, Download } from "lucide-react";
import { TablePager } from "@/components/TablePager";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { toast } from "sonner";

const ROLE_COLORS = {
  super_admin: "bg-info/10 text-info border-info/30",
  admin: "bg-warning/10 text-warning border-warning/30",
  manager: "bg-success/10 text-success border-success/30",
  branch_manager: "bg-primary/10 text-primary border-primary/30",
  employee: "bg-muted text-muted-foreground",
};
const NONE = "__none__";

export default function Users() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editBranches, setEditBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filterTenant, setFilterTenant] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", tenant_id: "", branch_id: NONE });
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "employee", branch_id: NONE, password: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = isSuper && filterTenant !== "all" ? { tenant_id: filterTenant } : {};
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isSuper, filterTenant]);

  const fetchBranches = useCallback(async (tid) => {
    try {
      if (isSuper && !tid) return [];
      const { data } = await api.get("/branches", { params: isSuper ? { tenant_id: tid } : {} });
      return data;
    } catch { return []; }
  }, [isSuper]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuper) api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    else fetchBranches().then(setBranches);
  }, [isSuper, fetchBranches]);

  const create = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(form.password)) return toast.error("Password must be at least 8 characters and include both letters and numbers");
    setBusy(true);
    try {
      await api.post("/users", { ...form, branch_id: form.branch_id === NONE ? "" : form.branch_id });
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "employee", tenant_id: "", branch_id: NONE });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = async (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email || "", role: u.role, branch_id: u.branch_id || NONE, password: "" });
    setEditBranches(await fetchBranches(u.tenant_id));
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editForm.password && !isStrongPassword(editForm.password)) return toast.error("Password must be at least 8 characters and include both letters and numbers");
    setBusy(true);
    try {
      const payload = { name: editForm.name, email: editForm.email, branch_id: editForm.branch_id === NONE ? "" : editForm.branch_id };
      if (canChangeRole(editing)) payload.role = editForm.role;
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editing.user_id}`, payload);
      toast.success("User updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.user_id}`, { active: !u.active }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleMfa = async (u) => {
    try { await api.patch(`/users/${u.user_id}`, { mfa_required: !u.mfa_required }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const removeUser = async (u) => {
    try { await api.delete(`/users/${u.user_id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const resetMfa = async (u) => {
    try {
      await api.post(`/users/${u.user_id}/reset-mfa`);
      toast.success("Two-factor authentication reset");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const roleColumns = ["manager", "branch_manager"].includes(user.role)
    ? "name,email,password,branch"
    : "name,email,password,role,branch";

  const downloadTemplate = () => {
    const sample = ["manager", "branch_manager"].includes(user.role)
      ? "Jane Doe,jane@example.com,,Auckland"
      : "Jane Doe,jane@example.com,,employee,Auckland";
    const csv = `${roleColumns}\n${sample}\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "users_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const bulkUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      if (data.created) toast.success(`${data.created} user(s) created`);
      if (data.errors?.length && !data.created) toast.error(`No users created — ${data.errors.length} row(s) had errors`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const roleOptions = isSuper
    ? ["admin", "manager", "branch_manager", "employee"]
    : ["manager", "branch_manager"].includes(user.role) ? ["employee"] : ["manager", "branch_manager", "employee"];

  const canManage = (u) => {
    if (u.role === "super_admin") return false;
    if (isSuper) return true;
    if (user.role === "admin") return ["manager", "branch_manager", "employee"].includes(u.role);
    if (["manager", "branch_manager"].includes(user.role)) return u.role === "employee";
    return false;
  };
  const canEdit = (u) => canManage(u) || u.user_id === user.user_id;
  const canChangeRole = (u) => u && u.user_id !== user.user_id && !["manager", "branch_manager"].includes(user.role) && u.role !== "super_admin";
  const canDelete = (u) => u.user_id !== user.user_id && canManage(u);
  const editRoleOptions = isSuper ? ["admin", "manager", "branch_manager", "employee"] : ["manager", "branch_manager", "employee"];

  const q = search.trim().toLowerCase();
  const base = isSuper
    ? users.filter((u) => u.role !== "super_admin")
    : ["manager", "branch_manager"].includes(user.role) ? users.filter((u) => u.role === "employee") : users;
  const filtered = q
    ? base.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (ROLE_LABEL[u.role] || "").toLowerCase().includes(q) ||
        (u.branch_name || "").toLowerCase().includes(q) ||
        (u.tenant_name || "").toLowerCase().includes(q))
    : base;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{isSuper ? "All Users" : "Team"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{isSuper ? "Users across every tenant." : "Manage your organization's members."}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, email, branch…" className="w-64 pl-9 rounded-sm" data-testid="user-search" />
          </div>
          {isSuper && (
            <Select value={filterTenant} onValueChange={(v) => { setFilterTenant(v); setPage(1); }}>
              <SelectTrigger className="w-48 rounded-sm" data-testid="filter-tenant"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tenants</SelectItem>
                {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {(user.role === "admin" || user.role === "manager") && (
            <Button variant="outline" className="rounded-sm" onClick={() => { setBulkResult(null); setBulkOpen(true); }} data-testid="bulk-upload-btn">
              <Upload className="h-4 w-4 mr-2" />Bulk Upload
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-2" />New User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="space-y-2"><Label>Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" className="rounded-sm" /></div>
                <div className="space-y-2"><Label>Password <span className="text-muted-foreground text-xs">(min 8, letters &amp; numbers)</span></Label>
                  <PasswordInput required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" className="rounded-sm" />
                  <PasswordStrength value={form.password} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {isSuper && (
                    <div className="space-y-2"><Label>Tenant</Label>
                      <Select value={form.tenant_id} onValueChange={async (v) => { setForm({ ...form, tenant_id: v, branch_id: NONE }); setBranches(await fetchBranches(v)); }}>
                        <SelectTrigger data-testid="user-tenant" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2"><Label>Branch</Label>
                    <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                      <SelectTrigger data-testid="user-branch" className="rounded-sm"><SelectValue placeholder="No branch" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>No branch</SelectItem>
                        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy} className="rounded-sm" data-testid="user-submit">
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <UsersIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">{users.length === 0 ? "No users yet." : "No users match your search."}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Branch</TableHead>
                {isSuper && <TableHead>Tenant</TableHead>}
                <TableHead className="text-center">2FA</TableHead>
                <TableHead className="text-center">Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((u) => (
                <TableRow key={u.user_id} className="hover:bg-muted/50" data-testid={`user-row-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-full ${ROLE_COLORS[u.role]}`}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.branch_name || "—"}</TableCell>
                  {isSuper && <TableCell className="text-sm text-muted-foreground">{u.tenant_name || "—"}</TableCell>}
                  <TableCell className="text-center">
                    {u.role === "employee" ? (
                      <div className="flex flex-col items-center gap-0.5">
                        <Switch checked={!!u.mfa_required} disabled={!canManage(u)}
                          onCheckedChange={() => toggleMfa(u)} data-testid={`user-mfa-${u.user_id}`} />
                        <span className="text-[10px] text-muted-foreground">
                          {u.mfa_enrolled ? "Enrolled" : (u.mfa_required ? "Pending" : "Off")}
                        </span>
                      </div>
                    ) : (
                      <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`user-mfa-required-${u.user_id}`}>Required</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {u.role === "super_admin" ? <span className="text-xs text-muted-foreground">—</span> :
                      <Switch checked={u.active} disabled={u.user_id === user.user_id || !canManage(u)}
                        onCheckedChange={() => toggleActive(u)} data-testid={`user-toggle-${u.user_id}`} />}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit(u) && (
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.user_id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete(u) ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`user-delete-${u.user_id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid="delete-user-dialog">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes <span className="font-medium">{u.email}</span> and cannot be undone. Their claims history remains, but they will lose access immediately.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`user-delete-cancel-${u.user_id}`}>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => removeUser(u)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`user-delete-confirm-${u.user_id}`}>
                                Delete User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : u.user_id === user.user_id ? (
                        <span className="text-xs text-muted-foreground">You</span>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePager page={safePage} pageCount={pageCount} total={filtered.length} shown={paged.length}
          onChange={setPage} testId="users-pager" />
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" className="rounded-sm" /></div>
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={!canChangeRole(editing)}>
                  <SelectTrigger data-testid="edit-user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{editRoleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Branch</Label>
                <Select value={editForm.branch_id} onValueChange={(v) => setEditForm({ ...editForm, branch_id: v })}>
                  <SelectTrigger data-testid="edit-user-branch" className="rounded-sm"><SelectValue placeholder="No branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No branch</SelectItem>
                    {editBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Reset Password <span className="text-muted-foreground text-xs">(leave blank to keep)</span></Label>
              <PasswordInput value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="New password" data-testid="edit-user-password" className="rounded-sm" />
              {editForm.password && <PasswordStrength value={editForm.password} />}</div>
            {editing && editing.role !== "super_admin" && (
              <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Two-factor authentication</p>
                  <p className="text-xs text-muted-foreground">
                    {editing.mfa_enrolled ? `Enrolled (${editing.mfa_method === "email" ? "email code" : "authenticator app"})` : "Not enrolled"}
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="rounded-sm" disabled={!editing.mfa_enrolled} data-testid="reset-mfa-btn">
                      <ShieldOff className="h-4 w-4 mr-2" />Reset 2FA
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="reset-mfa-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset two-factor authentication?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {editing.name} will be signed out and prompted to set up two-factor authentication again at their next login. Use this when they've lost their device.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="reset-mfa-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => resetMfa(editing)} data-testid="reset-mfa-confirm">Reset 2FA</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-sm" data-testid="edit-user-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-upload-dialog">
          <DialogHeader><DialogTitle>Bulk upload users</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV with columns <span className="font-mono text-foreground">{roleColumns}</span>.
              Leave <span className="font-mono">password</span> blank to auto-generate a temporary one.
              {user.role === "manager" ? " You can add employees only." : " Roles allowed: manager, employee."}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={downloadTemplate} data-testid="bulk-template-btn">
                <Download className="h-4 w-4 mr-2" />Download template
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={bulkUpload} data-testid="bulk-file-input" />
                <Button asChild size="sm" className="rounded-sm" disabled={bulkBusy}>
                  <span>{bulkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Choose CSV & upload</span>
                </Button>
              </label>
            </div>
            {bulkResult && (
              <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="bulk-result">
                <p className="text-sm font-medium">{bulkResult.created} created · {bulkResult.errors?.length || 0} error(s)</p>
                {bulkResult.created_users?.some((u) => u.temp_password) && (
                  <div className="border border-border rounded-md p-3 bg-muted/40">
                    <p className="text-xs font-medium mb-2">Temporary passwords (share securely):</p>
                    <div className="space-y-1">
                      {bulkResult.created_users.filter((u) => u.temp_password).map((u) => (
                        <div key={u.email} className="flex justify-between text-xs font-mono">
                          <span className="truncate">{u.email}</span><span className="ml-3">{u.temp_password}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div className="border border-destructive/30 rounded-md p-3">
                    <p className="text-xs font-medium text-destructive mb-2">Rows skipped:</p>
                    <div className="space-y-1">
                      {bulkResult.errors.map((er, i) => (
                        <div key={i} className="text-xs"><span className="font-mono">Row {er.row} ({er.email})</span> — {er.error}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
